import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Clock, XCircle, AlertCircle, Pencil, Lock, Route as RouteIcon } from "lucide-react";

const statusMap = {
  pendente: { label: "Pendente", icon: Clock, color: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  conferido: { label: "Conferido", icon: CheckCircle2, color: "bg-green-500/15 text-green-300 border-green-500/30" },
  rejeitado: { label: "Rejeitado", icon: XCircle, color: "bg-red-500/15 text-red-300 border-red-500/30" },
  correcao: { label: "Correção", icon: AlertCircle, color: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
};

export default function History() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/closings/my-history").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    const out = Array.from(map.entries()).map(([date, items]) => {
      const total_e = items.reduce((s, x) => s + (x.entregues || 0), 0);
      const total_r = items.reduce((s, x) => s + (x.retornos || 0), 0);
      const total_f = items.reduce((s, x) => s + (x.fora_lista_qtd || 0), 0);
      const total = total_e + total_r;
      const sucesso = total > 0 ? ((total_e / total) * 100).toFixed(2) : "0.00";
      return { date, items, total_e, total_r, total_f, sucesso };
    });
    out.sort((a, b) => (a.date < b.date ? 1 : -1));
    return out;
  }, [rows]);

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-400">Meus envios</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-white">Histórico</h1>
      </div>

      {loading ? (
        <p className="text-slate-500">Carregando…</p>
      ) : grouped.length === 0 ? (
        <div className="rounded-2xl border border-slate-800 bg-[#131B2B] p-8 text-center text-sm text-slate-400">
          Você ainda não enviou fechamentos.
        </div>
      ) : (
        <div className="space-y-4" data-testid="driver-history-list">
          {grouped.map((g) => (
            <div key={g.date} className="rounded-2xl border border-slate-800 bg-[#131B2B] overflow-hidden" data-testid={`history-day-${g.date}`}>
              {/* Day header */}
              <div className="border-b border-slate-800 bg-[#0F1828] px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-blue-400">
                      {new Date(g.date + "T00:00:00").toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                    </p>
                    <p className="text-xs text-slate-500">
                      {g.items.length} rota{g.items.length !== 1 ? "s" : ""}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">Sucesso</p>
                    <p className="font-display text-lg font-bold text-blue-400">{g.sucesso}%</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <Cell label="Entregues" value={g.total_e} accent="text-green-400" />
                  <Cell label="Retornos" value={g.total_r} accent="text-amber-300" />
                  <Cell label="Fora" value={g.total_f} accent="text-red-300" />
                </div>
              </div>
              {/* Routes */}
              <div className="divide-y divide-slate-800">
                {g.items.map((r, idx) => {
                  const s = statusMap[r.status] || statusMap.pendente;
                  const Icon = s.icon;
                  const editable = r.status !== "conferido";
                  return (
                    <div
                      key={r.id}
                      onClick={() => editable && navigate(`/motorista/fechamento?id=${r.id}`)}
                      className={`flex items-center gap-3 p-4 ${editable ? "cursor-pointer transition hover:bg-[#1A2438]" : ""}`}
                      data-testid={`history-item-${r.id}`}
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-500/15 text-blue-300">
                        <RouteIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rota {idx + 1}</span>
                          {editable ? <Pencil className="h-3 w-3 text-blue-400" /> : <Lock className="h-3 w-3 text-green-400" />}
                        </div>
                        <p className="truncate font-display text-sm font-semibold text-slate-100">{r.rota || "—"}</p>
                        <p className="text-xs text-slate-500">
                          E: <span className="text-green-400">{r.entregues}</span> · R: <span className="text-amber-300">{r.retornos}</span> · F: <span className="text-red-300">{r.fora_lista_qtd}</span>
                        </p>
                      </div>
                      <Badge className={`gap-1 border ${s.color}`}>
                        <Icon className="h-3 w-3" /> {s.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Cell({ label, value, accent }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`font-display text-base font-bold ${accent}`}>{value ?? 0}</p>
    </div>
  );
}
